import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Smartphone,
  Landmark,
  Lock,
  Send,
  ShieldAlert,
  CheckCircle2,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { StatusChip } from './StatusChip';
import { ResolveDisbursementDialog } from './ResolveDisbursementDialog';
import { DISBURSEMENT_ITEM_META, DISBURSEMENT_BATCH_META, RETRY_CLASS_LABEL } from '../lib/state';
import { formatMoney } from '../lib/format';
import { payrollApi } from '../api/payrollApi';
import { getApiErrorMessage } from '@/lib/apiError';
import type { DisbursementBatch, DisbursementItem, DisbursementItemStatus, RunState, Currency } from '../types';

/** Display order for the per-status integrity chips. */
const ITEM_STATUS_ORDER: DisbursementItemStatus[] = [
  'paid',
  'submitted',
  'unknown',
  'pending',
  'failed',
  'reconciliation_required',
  'reversed',
];

interface DisbursementsTabProps {
  batch: DisbursementBatch | null;
  runState: RunState;
  currency: Currency;
  busy: boolean;
  onPrepare: () => void;
  onSubmit: () => void;
  /** Cancel a PREPARED (never-released) batch — no money has moved; unblocks
   *  Reopen and a fresh prepare. Absent = feature not wired by the page. */
  onCancel?: () => void;
  /** Silent run-detail refetch — used by live polling and after item actions. */
  onRefresh: () => void | Promise<void>;
}

export const DisbursementsTab: React.FC<DisbursementsTabProps> = ({
  batch,
  runState,
  currency,
  busy,
  onPrepare,
  onSubmit,
  onCancel,
  onRefresh,
}) => {
  const { t } = useTranslation('payroll');
  const confirm = useConfirm();
  const [retrying, setRetrying] = useState<string | null>(null);
  const [resolveItem, setResolveItem] = useState<DisbursementItem | null>(null);

  // Live refresh: while the batch is out with the connector, poll the run detail
  // every 5s so release + settlement progress appears without a manual reload.
  // Covers 'submitting' (async worker driving the hand-off) AND 'submitted'
  // (awaiting settlement) — both are non-terminal out-with-connector states.
  const inFlight = batch?.status === 'submitting' || batch?.status === 'submitted';
  useEffect(() => {
    if (!inFlight) return;
    const id = window.setInterval(() => {
      void onRefresh();
    }, 5000);
    return () => window.clearInterval(id);
  }, [inFlight, onRefresh]);

  async function retry(item: DisbursementItem) {
    const ok = await confirm({
      title: t('disbursement.retryTitle', { defaultValue: 'Retry this payment?' }),
      message: t('disbursement.retryMessage', {
        defaultValue:
          'A status check with the provider happens first — the payment is only re-sent if it is not already paid. Re-send {{amount}} to {{name}}?',
        amount: formatMoney(item.amount, currency),
        name: item.employeeName,
      }),
      confirmLabel: t('disbursement.retryConfirm', { defaultValue: 'Retry payment' }),
    });
    if (!ok) return;
    setRetrying(item.id);
    try {
      await payrollApi.retryDisbursement(item.id);
      toast.success(t('disbursement.retrySent', { defaultValue: 'Payment re-sent' }));
      await onRefresh();
    } catch (e: any) {
      // 409 not_retryable / conflict — surface the server's explanation.
      toast.error(
        getApiErrorMessage(e, t('disbursement.retryError', { defaultValue: 'Could not retry the payment' })),
        { duration: 6000 },
      );
    } finally {
      setRetrying(null);
    }
  }

  if (runState !== 'locked' && runState !== 'paid') {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Lock className="mb-3 h-8 w-8 text-muted-foreground" />
        <p className="font-medium text-charcoal">Payments unlock when the run is locked</p>
        <p className="text-sm text-muted-foreground">
          Lock the approved run to prepare a disbursement batch for your employees.
        </p>
      </div>
    );
  }

  const hasItems = batch && batch.items.length > 0;

  if (!hasItems) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <Send className="h-8 w-8 text-[#D9711E]" />
        <div>
          <p className="font-medium text-charcoal">Ready to pay</p>
          <p className="text-sm text-muted-foreground">
            Prepare a batch from this run’s net pay, then release it for settlement.
          </p>
        </div>
        <Button className="gap-2" disabled={busy} onClick={onPrepare}>
          <Send className="h-4 w-4" />
          Prepare disbursement batch
        </Button>
      </div>
    );
  }

  const prepared = batch!.status === 'prepared';
  const statusCounts = batch!.statusCounts ?? {};

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-muted/40 p-4">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
          <Meta label="Connector" value={batch!.connector} mono />
          <Meta label="Beneficiaries" value={String(batch!.itemCount)} />
          <Meta label="Total" value={formatMoney(batch!.totalAmount, currency)} />
          <div>
            <p className="text-xs text-muted-foreground">Status</p>
            <StatusChip meta={DISBURSEMENT_BATCH_META[batch!.status]} />
          </div>
        </div>
        {prepared && (
          <div className="flex items-center gap-2">
            {onCancel && (
              <Button variant="outline" className="gap-2 border-2 border-border font-medium text-charcoal hover:border-red-400" disabled={busy}
                onClick={async () => {
                  const ok = await confirm({
                    title: t('disbursement.cancelTitle', { defaultValue: 'Cancel this batch?' }),
                    message: t('disbursement.cancelMessage', {
                      defaultValue:
                        'Nothing has been released — no money has moved. Cancelling frees the run so it can be reopened or a fresh batch prepared.',
                    }),
                  });
                  if (ok) onCancel();
                }}>
                Cancel batch
              </Button>
            )}
            <Button className="gap-2" disabled={busy} onClick={onSubmit}>
              <Send className="h-4 w-4" />
              Release payment
            </Button>
          </div>
        )}
        {batch!.status === 'completed' && (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#166534]">
            <CheckCircle2 className="h-4 w-4" />
            Settled
          </span>
        )}
      </div>

      {/* Batch integrity: settled-vs-expected at a glance. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-border p-3">
        <div className="flex flex-wrap items-center gap-2">
          {ITEM_STATUS_ORDER.filter((s) => (statusCounts[s] ?? 0) > 0).map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5">
              <StatusChip meta={DISBURSEMENT_ITEM_META[s]} />
              <span className="text-xs font-semibold tabular-nums text-charcoal">
                {statusCounts[s]}
              </span>
            </span>
          ))}
        </div>
        <span className="text-sm text-muted-foreground">
          {t('disbursement.paidOfTotal', {
            defaultValue: 'Paid {{paid}} of {{total}}',
            paid: formatMoney(batch!.paidTotal, currency),
            total: formatMoney(batch!.totalAmount, currency),
          })}
        </span>
        {batch!.fullyReconciled && (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#166534]">
            <CheckCircle2 className="h-4 w-4" />
            {t('disbursement.fullyReconciled', { defaultValue: 'Fully reconciled' })}
          </span>
        )}
      </div>

      {prepared && (
        <div className="flex items-start gap-2 rounded-xl border border-[#FEF3C7] bg-[#FEF3C7]/40 p-3 text-xs text-[#92400E]">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Ready to release:</strong> this run already cleared the HR → Finance → Final
            approval chain, so releasing here executes an approved payment. Releasing simulates the
            connector and settles each payout.
          </span>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Employee</TableHead>
            <TableHead>Channel</TableHead>
            <TableHead>Destination</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>{t('disbursement.colActions', { defaultValue: 'Actions' })}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {batch!.items.map((it) => (
            <TableRow key={it.id}>
              <TableCell className="font-medium text-charcoal">
                {it.employeeName}
                {it.allocationSeq ? (
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    {t('salarySplit.share', { defaultValue: 'share {{n}}', n: it.allocationSeq })}
                  </span>
                ) : null}
              </TableCell>
              <TableCell>
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  {it.channel === 'mobile_money' ? (
                    <Smartphone className="h-4 w-4" />
                  ) : (
                    <Landmark className="h-4 w-4" />
                  )}
                  {it.provider}
                </span>
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">{it.accountMasked}</TableCell>
              <TableCell className="text-right tabular-nums text-charcoal">
                {formatMoney(it.amount, currency)}
              </TableCell>
              <TableCell>
                <StatusChip meta={DISBURSEMENT_ITEM_META[it.status]} />
              </TableCell>
              <TableCell>
                <ItemAction
                  item={it}
                  retrying={retrying === it.id}
                  onRetry={() => retry(it)}
                  onResolve={() => setResolveItem(it)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ResolveDisbursementDialog
        item={resolveItem}
        currency={currency}
        onClose={() => setResolveItem(null)}
        onResolved={() => void onRefresh()}
      />
    </div>
  );
};

/** Per-item action: Retry (when the failure class permits), a muted humanized
 *  class label otherwise, or Resolve for items parked for reconciliation. */
const ItemAction: React.FC<{
  item: DisbursementItem;
  retrying: boolean;
  onRetry: () => void;
  onResolve: () => void;
}> = ({ item, retrying, onRetry, onResolve }) => {
  const { t } = useTranslation('payroll');

  if (item.status === 'reconciliation_required') {
    return (
      <Button size="sm" variant="outline" onClick={onResolve}>
        {t('disbursement.resolve', { defaultValue: 'Resolve' })}
      </Button>
    );
  }

  if (item.status === 'failed') {
    if (item.retryClass === 'safe_to_retry' || item.retryClass === 'retry_with_status_check') {
      return (
        <Button size="sm" variant="outline" className="gap-1.5" disabled={retrying} onClick={onRetry}>
          {retrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
          {t('disbursement.retry', { defaultValue: 'Retry' })}
        </Button>
      );
    }
    if (item.retryClass === 'do_not_retry' || item.retryClass === 'manual_review_required') {
      return (
        <span
          className="text-xs text-muted-foreground"
          title={t('disbursement.noRetryHint', {
            defaultValue: 'Automated retry is not allowed for this failure — {{class}}.',
            class: RETRY_CLASS_LABEL[item.retryClass].toLowerCase(),
          })}
        >
          {RETRY_CLASS_LABEL[item.retryClass]}
        </span>
      );
    }
  }

  return <span className="text-xs text-muted-foreground">—</span>;
};

const Meta: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-sm font-medium text-charcoal ${mono ? 'font-mono' : ''}`}>{value}</p>
  </div>
);
